import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { Button, Modal } from "flowbite-react";
import { HiArrowSmRight, HiOutlineExclamation } from "react-icons/hi";
import { toast } from "react-toastify";
import axios from "../../config/axiosInstance";
import { NOTIFICATIONS } from "../../config/apiConfig";
import { formatFullDate, getAsyncQueryProtocol } from "./NotificationCenter";

/**
 * Avisos importantes na entrada do sistema.
 *
 * Notificação de prioridade alta ou urgente ainda não lida abre este modal na
 * entrada (carga do painel). Fechar — pelo botão ou pela ação de um aviso —
 * marca como lidos todos os exibidos. ESC e clique fora não fecham: o "lido"
 * precisa vir de um clique de quem leu.
 *
 * O que chega com o sistema já aberto fica no sino e aparece aqui na próxima
 * entrada, se continuar sem leitura.
 */

const LIMIT = 50;

const PRIORITY_BADGES = {
  URGENT: {
    label: "Urgente",
    className: "bg-red-50 text-red-700 ring-red-200",
  },
  HIGH: {
    label: "Alta",
    className: "bg-amber-50 text-amber-800 ring-amber-200",
  },
};

const ImportantNotificationsModal = ({
  userId,
  enabled,
  onOpenAsyncQueryResult,
}) => {
  const queryClient = useQueryClient();
  const queryKey = ["important-notifications", userId];

  const { data } = useQuery(
    queryKey,
    async () => {
      const { data: response } = await axios.get(NOTIFICATIONS.LIST, {
        params: { unreadOnly: true, priority: "URGENT,HIGH", limit: LIMIT },
      });
      return response?.data;
    },
    {
      enabled: Boolean(userId && enabled),
      /* Uma busca por entrada: com o sistema aberto o modal não reabre sozinho
         — quem avisa do que chegar depois é o sino. */
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const notifications = data?.notifications || [];
  if (!enabled || !notifications.length) return null;

  const restantes = Math.max(
    0,
    Number(data?.pagination?.total || 0) - notifications.length,
  );

  /* Fecha na hora e grava a leitura depois: se a gravação falhar, o painel
     não pode ficar travado atrás do modal. O pior caso é o aviso voltar na
     próxima entrada. O cache vazio impede o modal de reabrir se o Sidebar
     remontar nesta mesma entrada. */
  const fechar = () => {
    queryClient.setQueryData(queryKey, { notifications: [] });
    axios
      .put(NOTIFICATIONS.MARK_MANY_READ, {
        ids: notifications.map(({ id }) => id),
      })
      .then(() =>
        queryClient.invalidateQueries(["system-notifications", userId]),
      )
      .catch(() =>
        toast.error(
          "Não foi possível marcar os avisos como lidos. Eles voltam a aparecer na próxima entrada.",
        ),
      );
  };

  const renderAction = (notification) => {
    if (!notification.link) return null;

    const label = notification.actionLabel || "Abrir";
    const className =
      "mt-3 inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200";
    const protocol = onOpenAsyncQueryResult
      ? getAsyncQueryProtocol(notification.link)
      : null;

    // Consulta assíncrona abre no modal global, como no sino.
    if (protocol) {
      return (
        <button
          type="button"
          onClick={() => {
            fechar();
            onOpenAsyncQueryResult(protocol);
          }}
          className={className}
        >
          {label}
          <HiArrowSmRight className="h-4 w-4" />
        </button>
      );
    }

    return (
      <Link to={notification.link} onClick={fechar} className={className}>
        {label}
        <HiArrowSmRight className="h-4 w-4" />
      </Link>
    );
  };

  const total = notifications.length;

  return (
    <Modal show size="2xl" onClose={fechar}>
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600 ring-1 ring-inset ring-red-100">
            <HiOutlineExclamation className="h-6 w-6" />
          </span>
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {total === 1 ? "Aviso importante" : `${total} avisos importantes`}
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {total === 1
                ? "Ao fechar, ele fica marcado como lido."
                : "Ao fechar, todos ficam marcados como lidos."}
            </p>
          </div>
        </div>

        <ul className="mt-5 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {notifications.map((notification) => {
            const badge =
              PRIORITY_BADGES[String(notification.priority).toUpperCase()];
            return (
              <li
                key={notification.id}
                className="rounded-xl border border-slate-200 p-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  {badge && (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ring-inset ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                  )}
                  <time className="text-xs text-slate-500">
                    {formatFullDate(
                      notification.publishedAt || notification.createdAt,
                    )}
                  </time>
                </div>
                <h3 className="mt-2 text-sm font-semibold text-slate-900">
                  {notification.title}
                </h3>
                <p className="mt-1 whitespace-pre-line text-sm leading-6 text-slate-600">
                  {notification.description}
                </p>
                {renderAction(notification)}
              </li>
            );
          })}
        </ul>

        {restantes > 0 && (
          <p className="mt-3 text-xs text-slate-500">
            Mais {restantes}{" "}
            {restantes === 1 ? "aviso importante" : "avisos importantes"} no
            sino de notificações.
          </p>
        )}

        <div className="mt-5 flex justify-end">
          <Button
            className="primary w-full sm:w-auto"
            size="lg"
            onClick={fechar}
          >
            Fechar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ImportantNotificationsModal;
